import React from "react";
import Image from "next/image";

import { cn } from "@/lib/utils";
import { Product } from "@/app/global/components/stock-category-types";
import Link from "next/link";
import { DirectPaths } from "@/config/routes";
import { productRoute, ProductRoute } from "@/types/product";
import { toUnlistedShareSlug } from "@/app/alts/unlisted-shares/[...id]/slug";
import { useAuth } from "@/context/authcontext";
import { UsStockCategory } from "@/app/interface/global";
import { useProductsByCategoryQuery } from "../use-us-stocks";

const cardTheme = [
    {
        card: "#E9EAF3",
        header: "#E9EAF3",
    },
    {
        card: "#10B9811A",
        header: "#10B9811A",
    },
    {
        card: "#283A971A",
        header: "#283A971A",
    },
    {
        card: "#F59E0B1A",
        header: "#F59E0B1A",
    },
];

interface StockCategoryCardProps {
    category: UsStockCategory;
    className?: string;
    pageRoute?: ProductRoute;
}

/* function DayPerformance({ performance }: { performance: PriceMovement }) {
    const isUp = performance.one_day_percentage > 0;

    return (
        <span
            className={cn(
                "flex items-center justify-end gap-0.5 text-xs font-medium",
                isUp ? "text-emerald-600" : "text-red-600",
            )}
        >
            <span className="text-xs leading-none">{isUp ? "▲" : "▼"}</span>
            <span>
                {isUp ? "+" : "-"}${performance.one_day_percentage}
            </span>
        </span>
    );
} */

function ProductRow({ product, pageRoute }: { product: Product; pageRoute?: ProductRoute }) {
    const { isAuthenticated } = useAuth();

    return (
        <Link
            href={`${isAuthenticated ? process.env.ICM_GLOBAL_POST_URL + DirectPaths.GLOBAL : process.env.ICM_GLOBAL_URL}${pageRoute === productRoute["us-stocks"] ? DirectPaths.US_STOCKS : (pageRoute === productRoute["us-etfs"] && DirectPaths.US_ETFS) || ""}/${product.symbol + "/" + toUnlistedShareSlug(product.company_name)}`}
            className="border-bg-[#0E0F171A] flex gap-3 border-b bg-white px-5 py-3.5"
        >
            <div className="border-bg-[#E8E9ED] flex h-11 w-11 items-center justify-center overflow-hidden rounded-md border bg-white">
                <Image
                    src={product.company_logo}
                    alt={`${product.company_name} logo`}
                    width={27.08}
                    height={27.08}
                    className="h-full w-full object-cover brightness-105"
                />
            </div>
            <div className="min-w-0 flex-1">
                <p className="truncate text-[#0F1016]">
                    <span className="block text-base font-semibold leading-[130%] tracking-[0%]">
                        {product.company_name}
                    </span>
                    <span className="block text-sm leading-[160%] tracking-[1%]">
                        ${product.live_price.toFixed(2)}
                    </span>
                </p>
                {/* <p className="truncate text-xs text-[#6B7280]">{product.company_name}</p> */}
            </div>
            {/* <div className="flex-shrink-0 text-right">
                <p className="text-base font-semibold text-[#0F1016]">${product.live_price}</p>
                <DayPerformance performance={product.price_movement} />
            </div> */}
        </Link>
    );
}

function ProductRowLoader() {
    return (
        <div className="border-bg-[#0E0F171A] flex gap-3 border-b bg-white px-5 py-3.5">
            <div className="border-bg-[#E8E9ED] flex h-11 w-11 animate-pulse items-center justify-center overflow-hidden rounded-md border bg-gray-200">
                &nbsp;
            </div>
            <div className="min-w-0 flex-1">
                <p className="truncate text-[#0F1016]">
                    <span className="mt-1 block h-3 w-[50%] animate-pulse rounded bg-gray-200 text-base font-semibold leading-[130%] tracking-[0%]">
                        &nbsp;
                    </span>
                    <span className="mt-2 block h-2 w-[30%] animate-pulse rounded bg-gray-200 text-sm leading-[160%] tracking-[1%]">
                        &nbsp;
                    </span>
                </p>
                {/* <p className="truncate text-xs text-[#6B7280]">{product.company_name}</p> */}
            </div>
            {/* <div className="flex-shrink-0 text-right">
                <p className="text-base font-semibold text-[#0F1016]">${product.live_price}</p>
                <DayPerformance performance={product.price_movement} />
            </div> */}
        </div>
    );
}

export default function StockCategoryCard({
    category,
    className,
    pageRoute,
}: StockCategoryCardProps) {
    const { productsByCategoryData, isProductsByCategoryDataLoading } = useProductsByCategoryQuery({
        categoryIndex: category.index,
    });

    return (
        <article
            className={cn("w-full max-w-[270px] overflow-hidden rounded-2xl border border-[#0E0F171A]", className)}
            style={{ backgroundColor: cardTheme[category.index - 1].card }}
        >
            <header
                className="flex min-h-[88px] items-center gap-3 border-b border-white px-5 py-3"
                /* style={{ backgroundColor: cardTheme[category.index - 1].header }} */
            >
                <Image
                    src={category.image}
                    width={40}
                    height={40}
                    alt={""}
                    className="h-[40px] w-[40px] rounded"
                />
                <h3 className="text-base font-semibold text-[#0F1016]">{category.name}</h3>
            </header>

            <div className="flex flex-col">
                {isProductsByCategoryDataLoading ? (
                    <>
                        {([1, 2, 3] as number[]).map((product: number) => {
                            return (
                                <div key={product}>
                                    <ProductRowLoader />
                                </div>
                            );
                        })}
                    </>
                ) : (
                    <>
                        {productsByCategoryData?.data?.products &&
                        productsByCategoryData.data.products?.length > 0 ? (
                            productsByCategoryData.data.products.map((product, index) => {
                                return (
                                    index < 3 && (
                                        <ProductRow
                                            key={`${product.ibkr_conid}`}
                                            product={product}
                                            pageRoute={pageRoute}
                                        />
                                    )
                                );
                            })
                        ) : (
                            <span></span>
                        )}
                    </>
                )}
            </div>
        </article>
    );
}
